import { Injectable } from '@angular/core';

import {Subscription, tap} from "rxjs";
import {
  BooxAppService,
  BooxMessageService,
  BooxStorageService,
  BooxSessionService,
  ISessionModel,

} from 'lib-boox';
import {HeroLocalizationService, IHeroLocale} from "./hero-localization.service";
import {HeroDictionaryService} from "./hero-dictionary.service";

@Injectable({
  providedIn: 'root'
})
export class HeroClientService extends BooxAppService{

  subscriptions: Subscription = new Subscription();

  constructor(
    message: BooxMessageService,
    storage: BooxStorageService,
    session: BooxSessionService,
    private locale: HeroLocalizationService,
    private dictionary: HeroDictionaryService,

  ) { super(message, storage, session) }

  /**
   * Updates the application status and sends a corresponding message.
   * This method prevents duplicate logic for status updates.
   *
   * @param status - The new application status
   */
  override setStatus(status: string): void {
    this.message.to(`guest.status.${status}`, {}); // Send a status update message
    super.setStatus(status); // Update the state with the new status
  }


  /**
   * Boot the application by binding/subscribeing to the messages
   * and setting the initial state.
   */
  override boot() {
    // signal app is booting
    super.boot();
    // subscribe to the session update
    // this.message.on$('session.run.update').subscribe(({ data }) => this.session.updateSession(data['session'] as ISessionModel, { store: false }));
    // this.message.on$('session.run.clear').subscribe(() => this.session.$session.next(null));
    // listen for the `locale.run.update` message
    // and update the locale from the message
    this.message.on$('locale.run.update').subscribe(({data}) => this.updateLocale(data['locale'] as IHeroLocale));
    // ****************** boot done -> ready ******************
    this.ready();
  }

  /**
   * Ready the application by binding/subscribeing to the messages
   * and setting the initial state.
   */
  override ready() {

    // create a new subscription list to store all the subscriptions
    // of this app component so they can be easily unsubscribed
    this.subscriptions = new Subscription();
    /***** bind message comunication *****/
    // listen for the `app.run.start` message
    // add the message subscription to the subscriptions list
    // so it is easier to unsubscribe when the app is stopped
    this.subscriptions.add(this.message.on$('app.run.start').pipe(
      // send a message to the host to indicate that the app is now starting
      tap(() => this.start()),

      // process the session from the message
      tap(({data}) => !!data['session'] && this.updateSession(data['session'] as ISessionModel)),
      // process the locale from the message
      tap(({data}) => data['locale'] && this.updateLocale(data['locale'] as IHeroLocale)),


      // listen for future `session.run.update` message
      // and update the session without storing it in the local storage
      tap(() => this.subscriptions.add(this.message.on$('session.run.update').subscribe(({data}) => this.updateSession(data['session'] as ISessionModel)))),

      // listen for the app stop message
      tap(() => this.subscriptions.add(this.message.on$('app.run.stop').subscribe(() => this.stop()))),

      // send a message to the host to indicate that the app is now running
      tap(() => this.running())
    ).subscribe());

    super.ready();
  }

  override stop() {
    super.stop();
    this.subscriptions.unsubscribe();
    this.updateSession(null);
    this.stopped();
  }

  override stopped(): void {
    super.stopped();
    this.ready();
  }

  private updateLocale(locale: IHeroLocale) {
    const language_code = locale.language.code;
    // update the locale
    this.locale.locale$.next(locale as IHeroLocale);
    this.dictionary.load$(language_code, 'app').subscribe();
  }

  private updateSession(session: ISessionModel|null) {
    if(!session) { this.session.$session.next(null); return; }
    // update the session
    this.session.updateSession(session, { store: false });
    // inform the app of the session change
    this.message.to('session.run.updated', { session }, 'app');
  }
}
